import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  CheckCircle,
  X,
  Share2,
  Edit,
  Trash2,
  Flag,
  MoreVertical,
  AlertCircle,
  Calendar,
  User,
  Check
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import AnswerForm from '../../components/questions/AnswerForm';
import AnswerItem from '../../components/questions/AnswerItem';
import MarkdownRenderer from '../../components/MarkdownRenderer';

const QuestionDetailPage = () => {
  const { courseId, questionId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [question, setQuestion] = useState(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [repliesMap, setRepliesMap] = useState({});
  
  // Fetch question details
  useEffect(() => {
    const fetchQuestionDetails = async () => {
      try {
        setLoading(true);
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions/${questionId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch question details');
        }
        
        const data = await response.json();
        setQuestion(data);
        
        // Fetch replies for any answers that have them
        const answersWithReplies = data.answers.filter(answer => answer.replyCount > 0);
        if (answersWithReplies.length > 0) {
          await Promise.all(
            answersWithReplies.map(answer => fetchReplies(answer.id))
          );
        }
      } catch (error) {
        console.error('Error fetching question details:', error);
        setError(error.message);
        toast.error('Failed to load question details');
      } finally {
        setLoading(false);
      }
    };
    
    if (questionId) {
      fetchQuestionDetails();
    }
  }, [questionId]);
  
  // Fetch replies for an answer
  const fetchReplies = async (answerId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/answers/${answerId}/replies`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch replies');
      }
      
      const replies = await response.json();
      
      setRepliesMap(prevMap => ({
        ...prevMap,
        [answerId]: replies
      }));
      
      return replies;
    } catch (error) {
      console.error(`Error fetching replies for answer ${answerId}:`, error);
      return [];
    }
  };
  
  // Vote on question
  const handleQuestionVote = async (voteType) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions/${questionId}/vote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ voteType })
      });
      
      if (!response.ok) {
        throw new Error('Failed to vote');
      }
      
      const updatedQuestion = await response.json();
      
      // Update question state with new vote info
      setQuestion(prev => ({
        ...prev,
        upvotes: updatedQuestion.upvotes,
        downvotes: updatedQuestion.downvotes,
        userVote: updatedQuestion.userVote
      }));
      
      toast.success(`${voteType === 'upvote' ? 'Upvoted' : 'Downvoted'} question`);
    } catch (error) {
      console.error('Error voting on question:', error);
      toast.error('Failed to vote on question');
    }
  };
  
  // Toggle question solved status
  const handleToggleSolved = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions/${questionId}/solved`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to update question status');
      }
      
      const result = await response.json();
      
      // Update question state
      setQuestion(prev => ({
        ...prev,
        isSolved: result.isSolved
      }));
      
      toast.success(result.message);
    } catch (error) {
      console.error('Error updating question status:', error);
      toast.error('Failed to update question status');
    }
  };
  
  // Delete question
  const handleDeleteQuestion = async () => {
    if (!window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions/${questionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete question');
      }
      
      toast.success('Question deleted successfully');
      navigate(`/courses/${courseId}?tab=questions`);
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
    }
  };
  
  // Submit answer
  const handleSubmitAnswer = async (content) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/answers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          questionId,
          content
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit answer');
      }
      
      const newAnswer = await response.json();
      
      // Add new answer to question
      setQuestion(prev => ({
        ...prev,
        answers: [...prev.answers, newAnswer]
      }));
      
      setShowReplyForm(false);
      toast.success('Answer submitted successfully');
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Failed to submit answer');
    }
  };
  
  // Reply to answer
  const handleReplyToAnswer = async (answerId, content) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/answers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          questionId,
          parentAnswerId: answerId,
          content
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit reply');
      }
      
      const newReply = await response.json();
      
      // Update replies in state
      setRepliesMap(prevMap => ({
        ...prevMap,
        [answerId]: [...(prevMap[answerId] || []), newReply]
      }));
      
      // Update the answer's reply count
      setQuestion(prev => ({
        ...prev,
        answers: prev.answers.map(answer => 
          answer.id === answerId 
            ? { ...answer, replyCount: (answer.replyCount || 0) + 1 } 
            : answer
        )
      }));
      
      toast.success('Reply submitted successfully');
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error('Failed to submit reply');
    }
  };
  
  // Vote on answer
  const handleAnswerVote = async (answerId, voteType) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/answers/${answerId}/vote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ voteType })
      });
      
      if (!response.ok) {
        throw new Error('Failed to vote on answer');
      }
      
      const updatedAnswer = await response.json();
      
      // Update answer in state
      setQuestion(prev => ({
        ...prev,
        answers: prev.answers.map(answer => 
          answer.id === answerId 
            ? { 
                ...answer, 
                upvotes: updatedAnswer.upvotes, 
                downvotes: updatedAnswer.downvotes,
                userVote: updatedAnswer.userVote
              } 
            : answer
        )
      }));
      
      toast.success(`${voteType === 'upvote' ? 'Upvoted' : 'Downvoted'} answer`);
    } catch (error) {
      console.error('Error voting on answer:', error);
      toast.error('Failed to vote on answer');
    }
  };
  
  // Accept answer
  const handleAcceptAnswer = async (answerId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/answers/${answerId}/accept`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to accept answer');
      }
      
      const result = await response.json();
      
      // Update answer acceptance status and question solved status
      setQuestion(prev => ({
        ...prev,
        isSolved: result.isAccepted,
        answers: prev.answers.map(answer => ({
          ...answer,
          isAccepted: answer.id === answerId ? result.isAccepted : false
        }))
      }));
      
      toast.success(result.message);
    } catch (error) {
      console.error('Error accepting answer:', error);
      toast.error('Failed to accept answer');
    }
  };
  
  // Delete answer
  const handleDeleteAnswer = async (answerId) => {
    if (!window.confirm('Are you sure you want to delete this answer? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/answers/${answerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete answer');
      }
      
      // Remove answer from state
      setQuestion(prev => ({
        ...prev,
        answers: prev.answers.filter(answer => answer.id !== answerId)
      }));
      
      // Remove replies from state
      setRepliesMap(prevMap => {
        const newMap = { ...prevMap };
        delete newMap[answerId];
        return newMap;
      });
      
      toast.success('Answer deleted successfully');
    } catch (error) {
      console.error('Error deleting answer:', error);
      toast.error('Failed to delete answer');
    }
  };
  
  // Edit answer
  const handleEditAnswer = async (answerId, content) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/answers/${answerId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update answer');
      }
      
      const updatedAnswer = await response.json();
      
      // Update answer in state
      setQuestion(prev => ({
        ...prev,
        answers: prev.answers.map(answer => 
          answer.id === answerId 
            ? { ...answer, content: updatedAnswer.content, updatedAt: updatedAnswer.updatedAt }
            : answer
        )
      }));
      
      toast.success('Answer updated successfully');
    } catch (error) {
      console.error('Error updating answer:', error);
      toast.error('Failed to update answer');
    }
  };
  
  // Handle share
  const handleShare = () => {
    const url = window.location.href;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url)
        .then(() => toast.success('Link copied to clipboard'))
        .catch(() => toast.error('Failed to copy link'));
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        document.execCommand('copy');
        toast.success('Link copied to clipboard');
      } catch (err) {
        toast.error('Failed to copy link');
      } finally {
        document.body.removeChild(textarea);
      }
    }
    
    setShowDropdown(false);
  };
  
  // Format date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-6 h-6 border-t-2 border-b-2 border-red-500 rounded-full animate-spin mr-2"></div>
        <span className="text-gray-600 dark:text-gray-400">Loading question...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Error Loading Question
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          {error}
        </p>
        <button
          onClick={() => navigate(`/courses/${courseId}?tab=questions`)}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Back to Questions
        </button>
      </div>
    );
  }
  
  if (!question) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Question Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          The question you're looking for couldn't be found or you don't have access to it.
        </p>
        <Link
          to={`/courses/${courseId}?tab=questions`}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Back to Questions
        </Link>
      </div>
    );
  }
  
  const isOwner = user && question.userId === user.id;
  const isModOrAdmin = user && (user.role === 'moderator' || user.role === 'admin');
  const canAcceptAnswer = isOwner || isModOrAdmin;
  
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Navigation breadcrumbs */}
      <div className="flex items-center mb-6 text-sm">
        <Link 
          to={`/courses/${courseId}`} 
          className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
        >
          {question.courseCode}: {question.courseName}
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <Link 
          to={`/courses/${courseId}?tab=questions`} 
          className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
        >
          Questions
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          {question.title?.substring(0, 30)}{question.title?.length > 30 ? '...' : ''}
        </span>
      </div>
      
      {/* Question */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row">
          {/* Vote column */}
          <div className="flex md:flex-col items-center md:items-center space-x-4 md:space-x-0 mr-6">
            <button
              onClick={() => handleQuestionVote('upvote')}
              className={`p-2 rounded-full ${
                question.userVote === 'upvote'
                  ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400'
              }`}
            >
              <ThumbsUp size={24} />
            </button>
            
            <span className="text-xl font-medium text-gray-700 dark:text-gray-300 md:my-3">
              {question.upvotes - question.downvotes}
            </span>
            
            <button
              onClick={() => handleQuestionVote('downvote')}
              className={`p-2 rounded-full ${
                question.userVote === 'downvote'
                  ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400'
              }`}
            >
              <ThumbsDown size={24} />
            </button>
          </div>
          
          {/* Question content */}
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {question.title}
                </h1>
                
                <div className="flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                  <span className="flex items-center mr-4 mb-2">
                    <User size={16} className="mr-1" />
                    <Link 
                      to={`/users/${question.userId}`}
                      className="hover:text-red-600 dark:hover:text-red-400"
                    >
                      {question.userName}
                    </Link>
                  </span>
                  <span className="flex items-center mr-4 mb-2">
                    <Calendar size={16} className="mr-1" />
                    {formatDate(question.createdAt)}
                  </span>
                  <span className="flex items-center mb-2">
                    <MessageSquare size={16} className="mr-1" />
                    {question.answers?.length || 0} {question.answers?.length === 1 ? 'answer' : 'answers'}
                  </span>
                </div>
                
                {/* Section tag */}
                {question.semester && question.year && (
                  <div className="mb-4">
                    <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                      Section: {question.semester} {question.year}
                      {question.professorName && ` • ${question.professorName}`}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-2 mb-4 md:mb-0">
                {question.isSolved === 1 && (
                  <span className="flex items-center px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-sm font-medium">
                    <CheckCircle size={16} className="mr-1" />
                    Solved
                  </span>
                )}
                
                {(isOwner || isModOrAdmin) && (
                  <button
                    onClick={handleToggleSolved}
                    className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      question.isSolved
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    }`}
                  >
                    {question.isSolved ? (
                      <>
                        <X size={16} className="mr-1" />
                        Mark Unsolved
                      </>
                    ) : (
                      <>
                        <Check size={16} className="mr-1" />
                        Mark Solved
                      </>
                    )}
                  </button>
                )}
                
                {/* Dropdown menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-full focus:outline-none"
                  >
                    <MoreVertical size={20} />
                  </button>
                  
                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-[9999] border border-gray-200 dark:border-gray-700">
                      <ul className="py-1">
                        <li>
                          <button
                            onClick={handleShare}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Share2 size={16} className="mr-2" />
                            Share
                          </button>
                        </li>
                        
                        {(isOwner || isModOrAdmin) && (
                          <>
                            <li>
                              <Link
                                to={`/courses/${courseId}/questions/${questionId}/edit`}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <Edit size={16} className="mr-2" />
                                Edit
                              </Link>
                            </li>
                            <li>
                              <button
                                onClick={handleDeleteQuestion}
                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <Trash2 size={16} className="mr-2" />
                                Delete
                              </button>
                            </li>
                          </>
                        )}
                        
                        {!isOwner && (
                          <li>
                            <button
                              onClick={() => toast.error('Report functionality coming soon')}
                              className="flex items-center w-full px-4 py-2 text-sm text-yellow-600 dark:text-yellow-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <Flag size={16} className="mr-2" />
                              Report
                            </button>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-4">
              <MarkdownRenderer content={question.content} />
            </div>
          </div>
        </div>
      </div>
      
      {/* Answers */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {question.answers?.length || 0} {question.answers?.length === 1 ? 'Answer' : 'Answers'}
          </h2>
          
          <button
            onClick={() => setShowReplyForm(!showReplyForm)}
            className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <MessageSquare size={16} className="mr-1" />
            {showReplyForm ? 'Cancel Answer' : 'Add Answer'}
          </button>
        </div>
        
        {/* Answer form */}
        {showReplyForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Your Answer
            </h3>
            <AnswerForm onSubmit={handleSubmitAnswer} onCancel={() => setShowReplyForm(false)} />
        </div>
        )}
        
        {/* Accepted answer (if any) */}
        {question.answers && question.answers.filter(answer => answer.isAccepted).length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-green-600 dark:text-green-400 mb-4 flex items-center">
              <CheckCircle size={20} className="mr-2" />
              Accepted Solution
            </h3>
            
            {question.answers
              .filter(answer => answer.isAccepted)
              .map(answer => (
                <AnswerItem
                  key={answer.id}
                  answer={answer}
                  onVote={(voteType) => handleAnswerVote(answer.id, voteType)}
                  onAccept={() => handleAcceptAnswer(answer.id)}
                  onDelete={() => handleDeleteAnswer(answer.id)}
                  onEdit={(content) => handleEditAnswer(answer.id, content)}
                  onReply={(content) => handleReplyToAnswer(answer.id, content)}
                  replies={repliesMap[answer.id] || []}
                  canAcceptAnswer={canAcceptAnswer}
                  isAccepted={answer.isAccepted}
                  userId={user?.id}
                  userRole={user?.role}
                  className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20"
                />
              ))}
          </div>
        )}
        
        {/* Other answers */}
        {question.answers && question.answers.filter(answer => !answer.isAccepted).length > 0 ? (
          <div className="space-y-6">
            {question.answers
              .filter(answer => !answer.isAccepted)
              .map(answer => (
                <AnswerItem
                  key={answer.id}
                  answer={answer}
                  onVote={(voteType) => handleAnswerVote(answer.id, voteType)}
                  onAccept={() => handleAcceptAnswer(answer.id)}
                  onDelete={() => handleDeleteAnswer(answer.id)}
                  onEdit={(content) => handleEditAnswer(answer.id, content)}
                  onReply={(content) => handleReplyToAnswer(answer.id, content)}
                  replies={repliesMap[answer.id] || []}
                  canAcceptAnswer={canAcceptAnswer}
                  isAccepted={answer.isAccepted}
                  userId={user?.id}
                  userRole={user?.role}
                />
              ))}
          </div>
        ) : (
          !question.answers?.filter(answer => answer.isAccepted).length && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center">
              <MessageSquare className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Answers Yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Be the first to answer this question!
              </p>
              <button
                onClick={() => setShowReplyForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <MessageSquare size={20} className="mr-2" />
                Write an Answer
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default QuestionDetailPage;